import { ReactNode, useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../stores/auth-store';
import type { UserRole } from '../types';

// Loading spinner component
function LoadingSpinner() {
  return (
    <div className="min-h-screen bg-cream flex items-center justify-center">
      <div className="flex flex-col items-center gap-4">
        <div className="w-12 h-12 border-4 border-sage/30 border-t-sage rounded-full animate-spin" />
        <p className="text-charcoal/70">Loading...</p>
      </div>
    </div>
  );
}

interface AuthGuardProps {
  children: ReactNode;
}

/**
 * Requires user to be authenticated.
 * Redirects to login if not authenticated.
 */
export function RequireAuth({ children }: AuthGuardProps) {
  const { isAuthenticated, isLoading, hasInitialized } = useAuthStore();
  const location = useLocation();

  // Only show the full-screen spinner during initial auth determination.
  // After init has completed once, isLoading toggling (e.g. from signIn)
  // must NOT unmount the children — that would wipe form state and any
  // post-submit error messages on Login/Signup.
  if (isLoading && !hasInitialized) {
    return <LoadingSpinner />;
  }

  if (!isAuthenticated) {
    // Redirect to login, preserving the intended destination
    return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  }

  return <>{children}</>;
}

/**
 * Redirects authenticated users away from public pages (login, signup).
 * Use on auth pages to redirect logged-in users to the app.
 */
export function RedirectIfAuthenticated({ children }: AuthGuardProps) {
  const { isAuthenticated, isLoading, hasInitialized } = useAuthStore();
  const location = useLocation();

  // Same reasoning as RequireAuth — once init has completed, never unmount
  // the auth form just because an in-flight signIn/signUp toggled isLoading.
  if (isLoading && !hasInitialized) {
    return <LoadingSpinner />;
  }

  // A creator invite link must stay on the signup page after auth: Signup accepts the
  // invite (role -> creator) and then routes to /creator. Redirecting here unmounted it
  // first, so invited physicians landed in /app as learners with the invite still pending.
  const hasInvite = new URLSearchParams(location.search).has('invite');

  if (isAuthenticated && !hasInvite) {
    // Redirect to intended destination or default to app
    const from = (location.state as { from?: string })?.from || '/app';
    return <Navigate to={from} replace />;
  }

  return <>{children}</>;
}

interface RoleGuardProps {
  children: ReactNode;
  allowedRoles: UserRole[];
  fallback?: ReactNode;
  redirectTo?: string;
}

/**
 * Requires user to have one of the allowed roles.
 * Must be used inside RequireAuth.
 */
export function RequireRole({
  children,
  allowedRoles,
  fallback,
  redirectTo = '/app',
}: RoleGuardProps) {
  const { user, isLoading, hasInitialized } = useAuthStore();

  if (isLoading && !hasInitialized) {
    return <LoadingSpinner />;
  }

  if (!user || !allowedRoles.includes(user.role)) {
    if (fallback) {
      return <>{fallback}</>;
    }
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}

/**
 * Requires user to be a creator (or admin).
 */
export function RequireCreator({ children }: AuthGuardProps) {
  return (
    <RequireRole allowedRoles={['creator', 'admin']} redirectTo="/app">
      {children}
    </RequireRole>
  );
}

/**
 * Requires user to be an admin.
 */
export function RequireAdmin({ children }: AuthGuardProps) {
  return (
    <RequireRole allowedRoles={['admin']} redirectTo="/app">
      {children}
    </RequireRole>
  );
}

/**
 * Requires user to be an org admin (or system admin).
 */
export function RequireOrgAdmin({ children }: AuthGuardProps) {
  return (
    <RequireRole allowedRoles={['org_admin', 'admin']} redirectTo="/app">
      {children}
    </RequireRole>
  );
}

interface SubscriptionGuardProps {
  children: ReactNode;
  requiredTier: 'premium' | 'pro';
  fallback?: ReactNode;
}

/**
 * Requires user to have a specific subscription tier.
 */
export function RequireSubscription({
  children,
  requiredTier,
  fallback,
}: SubscriptionGuardProps) {
  const { user, isLoading, hasInitialized } = useAuthStore();

  if (isLoading && !hasInitialized) {
    return <LoadingSpinner />;
  }

  const tierHierarchy = ['free', 'premium', 'pro'];
  const userTierIndex = tierHierarchy.indexOf(user?.subscription_tier || 'free');
  const requiredTierIndex = tierHierarchy.indexOf(requiredTier);

  if (userTierIndex < requiredTierIndex) {
    if (fallback) {
      return <>{fallback}</>;
    }
    return <Navigate to="/app/subscription" state={{ requiredTier }} replace />;
  }

  return <>{children}</>;
}

/**
 * Upgrade prompt for subscription-gated features.
 */
export function SubscriptionUpgradePrompt({ requiredTier }: { requiredTier: 'premium' | 'pro' }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] p-8 text-center bg-gradient-to-br from-sage/5 to-coral/5 rounded-xl">
      <div className="w-16 h-16 bg-sage/10 rounded-full flex items-center justify-center mb-6">
        <span className="text-3xl">✨</span>
      </div>
      <h2 className="text-2xl font-bold text-charcoal mb-2">
        Upgrade to {requiredTier === 'premium' ? 'Premium' : 'Pro'}
      </h2>
      <p className="text-charcoal/70 mb-6 max-w-md">
        This feature requires a {requiredTier} subscription.
        Upgrade to unlock unlimited questions, advanced features, and more.
      </p>
      <a
        href="/app/subscription"
        className="px-6 py-3 bg-sage text-white rounded-lg hover:bg-sage/90 transition-colors"
      >
        View Plans
      </a>
    </div>
  );
}

/**
 * Access denied component for unauthorized access attempts.
 */
export function AccessDenied({ message }: { message?: string }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] p-8 text-center">
      <div className="w-16 h-16 bg-rose/10 rounded-full flex items-center justify-center mb-6">
        <span className="text-3xl">🚫</span>
      </div>
      <h2 className="text-2xl font-bold text-charcoal mb-2">Access Denied</h2>
      <p className="text-charcoal/70 mb-6 max-w-md">
        {message || "You don't have permission to access this page."}
      </p>
      <a
        href="/app"
        className="px-6 py-3 bg-sage text-white rounded-lg hover:bg-sage/90 transition-colors"
      >
        Go to Dashboard
      </a>
    </div>
  );
}

/**
 * Hook to check if user has required role.
 */
export function useHasRole(allowedRoles: UserRole[]): boolean {
  const { user } = useAuthStore();
  return user ? allowedRoles.includes(user.role) : false;
}

/**
 * Hook to check if user has required subscription.
 */
export function useHasSubscription(requiredTier: 'free' | 'premium' | 'pro'): boolean {
  const { user } = useAuthStore();
  const tierHierarchy = ['free', 'premium', 'pro'];
  const userTierIndex = tierHierarchy.indexOf(user?.subscription_tier || 'free');
  const requiredTierIndex = tierHierarchy.indexOf(requiredTier);
  return userTierIndex >= requiredTierIndex;
}

/**
 * Component to conditionally render based on role.
 */
export function ShowForRole({
  roles,
  children,
  fallback = null,
}: {
  roles: UserRole[];
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const hasRole = useHasRole(roles);
  return hasRole ? <>{children}</> : <>{fallback}</>;
}

/**
 * Component to conditionally render based on subscription.
 */
export function ShowForSubscription({
  tier,
  children,
  fallback = null,
}: {
  tier: 'premium' | 'pro';
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const hasSubscription = useHasSubscription(tier);
  return hasSubscription ? <>{children}</> : <>{fallback}</>;
}
